/* @flow */

import * as React from 'react';

import type Moment from 'moment';
import moment from 'moment';

import {Avatar} from 'shells/avatar';
import type {TwoLineOptionWithIcon} from 'shells/select-two-line-icon';

import type {GetActivityForModal_activity_participants as Participant} from 'nutshell-graphql-types';

import type {Option} from '../../../core/select/graphql-select-inline';

import {AttendeeAddButton} from './attendee-add-button';
import type {ExternalAttendeeToContactData} from './participants-section';

export const getTimeForSelectedDate = (date: Moment, time: Moment): Moment => {
    return moment(date).set({hour: time.hour(), minute: time.minute(), second: 0, millisecond: 0});
};

// If the startTime is updated, we need to update the endTime if it is invalid (less than startTime)
export const getValidEndTime = (
    newStartTime: Moment,
    endTime: Moment,
    timeDuration: number
): Moment => {
    const newEndTime = newStartTime.clone().add(timeDuration, 's');

    return newEndTime;
};

// If the endTime is updated, we need to update the startTime if it is invalid (greater than endTime)
export const getValidStartTime = (startTime: Moment, newEndTime: Moment): Moment => {
    if (newEndTime.isBefore(startTime)) {
        const newStartTime = newEndTime.clone().subtract(15, 'm');
        const startOfDay = newEndTime.clone().startOf('day');

        // Don't allow the start time to go before 12:00 AM of the same day
        if (newStartTime.isBefore(startOfDay)) {
            return startOfDay;
        }

        return newStartTime;
    }

    return startTime;
};

export const mapSelectOptionsToListOptions = (
    options: Option[],
    handleCreateContactFromExternalAttendee: (data: ExternalAttendeeToContactData) => void,
    isCreatingOrMergingContact: boolean
): TwoLineOptionWithIcon<Option>[] => {
    return options.map((option) => {
        const href = option.metaData && option.metaData.href;
        const userEmail = option.type === 'users' ? stripHtml(option.relatedName || '') : null;
        const subLabel =
            option.emailAddress || stripHtml(option.primaryInfo || '') || userEmail || '';

        const actions =
            option.type === 'activityExternalAttendees'
                ? [
                      <AttendeeAddButton
                          key={`attendee-action-${option.id}`}
                          option={option}
                          handleCreateContactFromExternalAttendee={
                              handleCreateContactFromExternalAttendee
                          }
                          isCreatingOrMergingContact={isCreatingOrMergingContact}
                      />,
                  ]
                : [];

        return {
            label: option.name,
            value: option.id,
            sublabel: subLabel,
            iconNode: (
                <Avatar
                    type={option.type}
                    avatarUrl={option.avatarUrl}
                    initials={option.initials}
                />
            ),
            href,
            metaData: {
                ...option,
                actions,
            },
        };
    });
};

export const mapParticipantsToSelectOptions = (participants: Participant[]): Option[] => {
    return participants.map((participant) => {
        return {
            id: participant.id,
            name: participant.name,
            initials: participant.initials || '',
            avatarUrl: participant.avatarUrl,
            emailAddress: participant.primaryEmailAddress || '',
            primaryInfo: participant.primaryPhoneNumber || '',
            type: participant.type,
            metaData: {
                href: participant.htmlUrl,
            },
        };
    });
};

export const sortParticipantsOnAdd = (options: Option | Option[]): Option | Option[] => {
    if (!Array.isArray(options)) {
        return options;
    }

    return [...options].sort((a: Option, b: Option) => {
        if (a.type === 'activityExternalAttendees') {
            return 1;
        }
        if (b.type === 'activityExternalAttendees') {
            return -1;
        }

        return 0;
    });
};

export const mapListOptionsToSelectOptions = (
    options: TwoLineOptionWithIcon<Option>[]
): Option[] => {
    return options.map((option) => {
        const href = option.metaData && option.metaData.metaData && option.metaData.metaData.href;

        return {
            id: option.value,
            name: option.label,
            initials: (option.metaData && option.metaData.initials) || '',
            avatarUrl: option.metaData && option.metaData.avatarUrl,
            emailAddress: (option.metaData && option.metaData.emailAddress) || '',
            primaryInfo: option.metaData && option.metaData.primaryInfo,
            type: option.metaData && option.metaData.type,
            metaData: {
                href,
            },
        };
    });
};

export const checkMultiDay = (startTime: number, endTime: number): boolean => {
    const startTimeDate = moment.unix(startTime).format('DD/MM/YYYY');
    const endTimeDate = moment.unix(endTime).format('DD/MM/YYYY');

    return startTimeDate !== endTimeDate;
};

export const stripHtml = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    return temp.textContent || temp.innerText || '';
};

export const getHasNonCreatorParticipants = (activity: Activity): boolean => {
    const creatorUser = activity.creatorUser;
    if (!creatorUser) {
        return false;
    }

    return activity.participants.some((participant) => participant.name !== creatorUser.name);
};

export const getBannerSaveText = (activity: Activity): string => {
    // There are two cases where we consider this activity syncing for sake of
    // showing proper copy:
    // 1. The activity is already connected to an external calendar
    // 2. The activity is not yet connected to an external calendar, but it is
    //    set to sync to the calendar
    const isSyncing = activity.isConnectedToExternalCalendar || activity.shouldSyncToCalendar;
    const hasNonCreatorParticipants = getHasNonCreatorParticipants(activity);

    const isExternalSyncOrigin =
        activity.creatorOrigin &&
        (activity.creatorOrigin.originType === 'GOOGLE_API' ||
            activity.creatorOrigin.originType === 'OFFICE365_API');

    // When an activity is created in Nutshell, we dont add participants to the
    // external event. In the case of warning against notifying attendees, we
    // instead only need to check for activities where external participants may
    // be added, such as activities synced in from external sources or Scheduler
    const hasExternalAddedParticipants = isExternalSyncOrigin || activity.hasBooking;

    if (isSyncing && hasNonCreatorParticipants && hasExternalAddedParticipants) {
        return 'Changes made to this event may notify your attendees';
    }

    return 'Save your changes to this event';
};
